import { useReducer, useState, useRef, useEffect, type FormEvent } from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { useStaffMessage } from '@/Public/StaffMessage/Hooks/useStaffMessage';
import { ensureRecaptcha } from '@/Public/StaffMessage/RecaptchaLoader';
import styles from './StaffMessageForm.module.css';

// ── State machine ─────────────────────────────────────────────────────────────

type Phase =
    | 'idle'          // email not yet validated
    | 'validating'    // validateSender in flight
    | 'ready'         // validated, no OTP needed
    | 'otp'           // OTP section shown
    | 'otp-resending' // resendOtp in flight
    | 'sending'       // sendEmail in flight
    | 'success';

interface FieldErrors {
    readonly name: string | null;
    readonly email: string | null;
    readonly subject: string | null;
    readonly message: string | null;
}

interface State {
    readonly phase: Phase;
    readonly senderIdHash: string | null;
    readonly hadOtp: boolean;
    readonly bannerError: string | null;
    readonly resendNotice: string | null;
    readonly resendNoticeNonce: number;
    readonly otpError: string | null;
    readonly resendHidden: boolean;
    readonly resendCooldownUntil: number | null;
    readonly fieldErrors: FieldErrors;
    // Field values
    readonly name: string;
    readonly email: string;
    readonly phone: string;
    readonly subject: string;
    readonly message: string;
    readonly otpCode: string;
}

const noFieldErrors: FieldErrors = { name: null, email: null, subject: null, message: null };

const initialState: State = {
    phase: 'idle',
    senderIdHash: null,
    hadOtp: false,
    bannerError: null,
    resendNotice: null,
    resendNoticeNonce: 0,
    otpError: null,
    resendHidden: false,
    resendCooldownUntil: null,
    fieldErrors: noFieldErrors,
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    otpCode: '',
};

type Action =
    | { type: 'SET_FIELD'; field: 'name' | 'email' | 'phone' | 'subject' | 'message' | 'otpCode'; value: string }
    | { type: 'VALIDATE_START' }
    | { type: 'VALIDATE_SUCCESS'; senderIdHash: string; requiresOtp: boolean; cooldownUntil: number }
    | { type: 'VALIDATE_ERROR'; bannerMessage: string }
    | { type: 'RESEND_START' }
    | { type: 'RESEND_SUCCESS'; cooldownUntil: number }
    | { type: 'RESEND_RATE_LIMITED' }
    | { type: 'SEND_START' }
    | { type: 'SEND_SUCCESS' }
    | { type: 'SEND_RATE_LIMITED' }
    | { type: 'SEND_OTP_MISMATCH' }
    | { type: 'SEND_OTP_EXPIRED' }
    | { type: 'SEND_SENDER_MISMATCH' }
    | { type: 'CAPTCHA_FAILED' }
    | { type: 'DISMISS_BANNER' }
    | { type: 'FIELD_ERRORS'; errors: FieldErrors }
    | { type: 'HIDE_RESEND_NOTICE' }
    | { type: 'SEND_ANOTHER' };

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'SET_FIELD': {
            const base = { ...state, [action.field]: action.value };
            // Clearing a field error when the user edits it
            if (action.field in state.fieldErrors) {
                base.fieldErrors = { ...state.fieldErrors, [action.field]: null };
            }
            // If user changes email after success, they need to re-validate
            if (action.field === 'email' && state.phase === 'success') {
                return { ...base, phase: 'idle', senderIdHash: null };
            }
            return base;
        }

        case 'VALIDATE_START':
            return {
                ...state,
                phase: 'validating',
                senderIdHash: null,
                hadOtp: false,
                bannerError: null,
                resendNotice: null,
                otpError: null,
                resendHidden: false,
                resendCooldownUntil: null,
                otpCode: '',
            };

        case 'VALIDATE_SUCCESS':
            return {
                ...state,
                phase: action.requiresOtp ? 'otp' : 'ready',
                senderIdHash: action.senderIdHash,
                hadOtp: action.requiresOtp,
                bannerError: null,
                resendNotice: action.requiresOtp ? 'A new verification code has been sent to the email address provided.' : null,
                resendNoticeNonce: action.requiresOtp ? state.resendNoticeNonce + 1 : state.resendNoticeNonce,
                resendCooldownUntil: action.requiresOtp ? action.cooldownUntil : null,
            };

        case 'VALIDATE_ERROR':
            return { ...state, phase: 'idle', bannerError: action.bannerMessage };

        case 'RESEND_START':
            return { ...state, phase: 'otp-resending', otpCode: '', otpError: null, bannerError: null, resendNotice: null };

        case 'RESEND_SUCCESS':
            return {
                ...state,
                phase: 'otp',
                otpError: null,
                resendNotice: 'A new verification code has been sent to the email address provided.',
                resendNoticeNonce: state.resendNoticeNonce + 1,
                resendCooldownUntil: action.cooldownUntil,
            };

        case 'RESEND_RATE_LIMITED':
            return {
                ...state,
                phase: 'otp',
                resendHidden: true,
                resendNotice: null,
                bannerError: 'You have requested too many verification codes. Please try again later.',
            };

        case 'SEND_START':
            return { ...state, phase: 'sending', bannerError: null, resendNotice: null, otpError: null };

        case 'SEND_SUCCESS':
            return { ...state, phase: 'success', bannerError: null };

        case 'SEND_RATE_LIMITED':
            return {
                ...state,
                phase: state.hadOtp ? 'otp' : 'ready',
                bannerError: "This message can't be sent right now. Please try again later.",
            };

        case 'SEND_OTP_MISMATCH':
            return {
                ...state,
                phase: 'otp',
                otpError:
                    "That code doesn't match. Please check the code in your email and try again, or request a new one.",
            };

        case 'SEND_OTP_EXPIRED':
            return {
                ...state,
                phase: 'otp',
                otpError: 'That code has expired. Please request a new one.',
            };

        case 'SEND_SENDER_MISMATCH':
            return {
                ...initialState,
                name: state.name,
                phone: state.phone,
                subject: state.subject,
                message: state.message,
                bannerError:
                    'There was a problem verifying your email address. Please re-enter it and try again.',
            };

        case 'CAPTCHA_FAILED':
            return {
                ...state,
                phase:
                    state.phase === 'sending'
                        ? state.hadOtp ? 'otp' : 'ready'
                        : state.phase === 'otp-resending'
                            ? 'otp'
                            : 'idle',
                resendNotice: null,
                bannerError: 'Security verification failed. Please refresh the page and try again.',
            };

        case 'DISMISS_BANNER':
            return { ...state, bannerError: null, resendNotice: null };

        case 'FIELD_ERRORS':
            return { ...state, fieldErrors: action.errors };

        case 'HIDE_RESEND_NOTICE':
            return { ...state, resendNotice: null };

        case 'SEND_ANOTHER':
            return initialState;
    }
}

// ── Resend cooldown ────────────────────────────────────────────────────────────

const RESEND_COOLDOWN_MS = 60_000;

function useSecondsRemaining(until: number | null): number {
    const compute = () => (until ? Math.max(0, Math.ceil((until - Date.now()) / 1000)) : 0);
    const [secondsLeft, setSecondsLeft] = useState(compute);
    useEffect(() => {
        if (!until) { setSecondsLeft(0); return; }
        setSecondsLeft(compute());
        const id = window.setInterval(() => {
            const s = compute();
            setSecondsLeft(s);
            if (s <= 0) window.clearInterval(id);
        }, 1000);
        return () => window.clearInterval(id);
    // compute is recreated each render but is a pure function of `until`;
    // listing only `until` as the dependency is intentional and correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [until]);
    return secondsLeft;
}

const formatCountdown = (s: number) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// ── Component ─────────────────────────────────────────────────────────────────

interface StaffMessageFormProps {
    readonly staffName: string;
    readonly recipientId?: string;
    readonly recipientAddress?: string;
    readonly recaptchaSiteKey: string;
    readonly emailVerificationRequired: boolean;
}

async function getRecaptchaToken(siteKey: string, action: string): Promise<string | null> {
    // No site key configured (e.g. RECAPTCHA_V3_SITE unset in this environment) — the
    // server is told to bypass verification via bypassRecaptcha, so there's no token to
    // fetch. Returning '' (not null) lets callers tell this apart from a real failure:
    // null means "recaptcha failed", '' means "recaptcha isn't configured, proceed anyway".
    if (!siteKey) return '';
    try {
        await ensureRecaptcha(siteKey);
    } catch {
        return null;
    }
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return null;
    return new Promise((resolve) => {
        // 10-second safety net: if grecaptcha.execute() never settles (e.g. Google
        // API unreachable mid-load), surface null so callers dispatch CAPTCHA_FAILED
        // instead of leaving the form stuck in a busy phase forever.
        const timer = window.setTimeout(() => resolve(null), 10_000);
        grecaptcha.ready(() => {
            grecaptcha.execute(siteKey, { action }).then(
                (token) => { window.clearTimeout(timer); resolve(token); },
                () => { window.clearTimeout(timer); resolve(null); },
            );
        });
    });
}

function validateFields(state: State): FieldErrors | null {
    let name: string | null = null;
    let email: string | null = null;
    let subject: string | null = null;
    let message: string | null = null;

    if (!state.name.trim()) name = 'Enter your name.';
    const emailVal = state.email.trim();
    if (!emailVal) {
        email = 'Enter your email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        email = 'Enter an email address in the format name@example.com';
    }
    if (!state.subject.trim()) subject = 'Enter a subject.';
    if (!state.message.trim()) message = 'Enter your message.';
    if (name === null && email === null && subject === null && message === null) return null;
    return { name, email, subject, message };
}

export function StaffMessageForm({
    staffName,
    recipientId,
    recipientAddress,
    recaptchaSiteKey,
    emailVerificationRequired,
}: StaffMessageFormProps): JSX.Element {
    const [state, dispatch] = useReducer(reducer, initialState);
    const lastValidatedEmail = useRef('');
    const otpInputRef = useRef<HTMLInputElement>(null);
    const bannerRef = useRef<HTMLDivElement>(null);
    const api = useStaffMessage();

    const showOtpSection =
        state.phase === 'otp' ||
        state.phase === 'otp-resending' ||
        (state.phase === 'sending' && state.hadOtp);

    const requiredFieldsFilled =
        state.name.trim() !== '' &&
        state.email.trim() !== '' &&
        state.subject.trim() !== '' &&
        state.message.trim() !== '';

    // 'idle' is allowed so browser autofill (which never blurs the email field) can still submit;
    // handleSubmit validates the sender before sending.
    const canSubmit =
        requiredFieldsFilled &&
        (!showOtpSection || state.otpCode.length >= 6) &&
        (state.phase === 'idle' || state.phase === 'ready' || state.phase === 'otp' || state.phase === 'success');

    const isBusy =
        state.phase === 'validating' ||
        state.phase === 'sending' ||
        state.phase === 'otp-resending';

    const resendSecondsLeft = useSecondsRemaining(state.resendCooldownUntil);
    const resendCoolingDown = resendSecondsLeft > 0;

    // Auto-focus the OTP input when the OTP section first appears
    useEffect(() => {
        if (showOtpSection && otpInputRef.current) {
            otpInputRef.current.focus();
        }
    }, [showOtpSection]);

    // Move focus to the banner when an error appears so screen readers announce it
    useEffect(() => {
        if (state.bannerError && bannerRef.current) {
            bannerRef.current.focus();
        }
    }, [state.bannerError]);

    // Hide the resend success notice after 10 seconds; the nonce resets the timer on repeated resends
    useEffect(() => {
        if (!state.resendNotice) return;
        const id = window.setTimeout(() => dispatch({ type: 'HIDE_RESEND_NOTICE' }), 10000);
        return () => window.clearTimeout(id);
    }, [state.resendNotice, state.resendNoticeNonce]);

    // ── Handlers ────────────────────────────────────────────────────────────

    // Returns whether an OTP step is now required, or null if validation did not succeed.
    const validateEmail = async (email: string): Promise<{ requiresOtp: boolean } | null> => {
        // NOTE: lastValidatedEmail is only updated on success, not before the
        // await, so a transient network error leaves the user able to retry.
        dispatch({ type: 'VALIDATE_START' });
        // captcha action must match the action registered in the backend.
        const recaptchaToken = await getRecaptchaToken(recaptchaSiteKey, 'lookup_sender');
        if (recaptchaToken === null) {
            dispatch({ type: 'CAPTCHA_FAILED' });
            return null;
        }
        const result = await api.validateSender.mutateAsync({ email, recaptchaToken, bypassRecaptcha: !recaptchaSiteKey });
        if ('error' in result) {
            if (result.error === 'captcha-failed') {
                dispatch({ type: 'CAPTCHA_FAILED' });
            } else {
                const bannerMessage =
                    result.error === 'rate-limited'
                        ? "This message can't be sent right now. Please try again later."
                        : 'There was a problem verifying your email address. Please try again.';
                dispatch({ type: 'VALIDATE_ERROR', bannerMessage });
            }
            return null;
        }
        lastValidatedEmail.current = email;
        const requiresOtp = emailVerificationRequired && result.requiresOtp;
        dispatch({
            type: 'VALIDATE_SUCCESS',
            senderIdHash: result.senderIdHash,
            requiresOtp,
            cooldownUntil: Date.now() + RESEND_COOLDOWN_MS,
        });
        return { requiresOtp };
    };

    const handleEmailBlur = async () => {
        const email = state.email.trim();
        if (!email || email === lastValidatedEmail.current) return;
        await validateEmail(email);
    };

    const handleResend = async () => {
        if (resendCoolingDown || !state.senderIdHash) return;
        dispatch({ type: 'RESEND_START' });
        const recaptchaToken = await getRecaptchaToken(recaptchaSiteKey, 'request_verification');
        if (recaptchaToken === null) {
            dispatch({ type: 'CAPTCHA_FAILED' });
            return;
        }
        const result = await api.requestOtp.mutateAsync({ email: state.email, recaptchaToken, bypassRecaptcha: !recaptchaSiteKey });
        if ('error' in result) {
            if (result.error === 'captcha-failed') {
                dispatch({ type: 'CAPTCHA_FAILED' });
            } else {
                dispatch({ type: 'RESEND_RATE_LIMITED' });
            }
        } else {
            dispatch({ type: 'RESEND_SUCCESS', cooldownUntil: Date.now() + RESEND_COOLDOWN_MS });
        }
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Client-side field validation
        const fieldErrors = validateFields(state);
        if (fieldErrors) {
            dispatch({ type: 'FIELD_ERRORS', errors: fieldErrors });
            return;
        }

        if (!state.senderIdHash) {
            const validated = await validateEmail(state.email.trim());
            // An OTP step is shown when required; the user submits again with the code.
            if (!validated || validated.requiresOtp) return;
        }

        dispatch({ type: 'SEND_START' });
        // Always send the trimmed email to match what was validated
        const trimmedEmail = state.email.trim();
        const recaptchaToken = await getRecaptchaToken(recaptchaSiteKey, 'send_message');
        if (recaptchaToken === null) {
            dispatch({ type: 'CAPTCHA_FAILED' });
            return;
        }
        const result = await api.sendMessage.mutateAsync({
            senderData: { name: state.name, email: trimmedEmail, phone: state.phone },
            messageData: { subject: state.subject, body: state.message },
            ...(recipientId ? { recipientId } : {}),
            ...(recipientAddress ? { recipientAddress } : {}),
            recaptchaToken,
            bypassRecaptcha: !recaptchaSiteKey,
            otpCode: state.hadOtp ? state.otpCode : undefined,
        });
        if ('error' in result) {
            switch (result.error) {
                case 'rate-limited':
                    dispatch({ type: 'SEND_RATE_LIMITED' });
                    break;
                case 'otp-mismatch':
                    dispatch({ type: 'SEND_OTP_MISMATCH' });
                    break;
                case 'otp-expired':
                    dispatch({ type: 'SEND_OTP_EXPIRED' });
                    break;
                case 'sender-mismatch':
                    lastValidatedEmail.current = '';
                    dispatch({ type: 'SEND_SENDER_MISMATCH' });
                    break;
                case 'captcha-failed':
                    dispatch({ type: 'CAPTCHA_FAILED' });
                    break;
                default:
                    dispatch({
                        type: 'VALIDATE_ERROR',
                        bannerMessage: 'Something went wrong. Please try again.',
                    });
            }
        } else {
            dispatch({ type: 'SEND_SUCCESS' });
        }
    };

    const handleSendAnother = () => {
        lastValidatedEmail.current = '';
        dispatch({ type: 'SEND_ANOTHER' });
    };

    const hasFieldErrors = Object.values(state.fieldErrors).some((v) => v !== null);

    // ── Success state ────────────────────────────────────────────────────────

    if (state.phase === 'success') {
        return (
            <div className={cx(styles.formBody, styles['formBody--success'])}>
                <div
                    className={cx(styles.alert, styles['alert--success'], styles['alert--lg'])}
                    role="status"
                >
                    <FontAwesomeIcon icon={faCircleCheck} className={styles.alertIcon} aria-hidden="true" />
                    <div>
                        <p className={styles.alertTitle}>
                            {state.hadOtp
                                ? 'Your email has been verified and your message has been sent.'
                                : 'Your message has been submitted.'}
                        </p>
                        {state.hadOtp && (
                            <p className={styles.alertSubtext}>
                                You won&apos;t need to verify again next time you write from this
                                address.
                            </p>
                        )}
                    </div>
                </div>
                <button type="button" className={styles.button} onClick={handleSendAnother}>
                    Send another message
                </button>
            </div>
        );
    }

    // ── Form ─────────────────────────────────────────────────────────────────

    return (
        <form
            className={styles.form}
            aria-label={`Send a message to ${staffName}`}
            onSubmit={handleSubmit}
            aria-busy={isBusy}
            noValidate
        >
            {/* Error banner (replaces portalled toast) */}
            {(state.bannerError || hasFieldErrors) && (
                <div
                    className={cx(styles.alert, styles['alert--error'])}
                    role="alert"
                    aria-live="assertive"
                    tabIndex={-1}
                    ref={bannerRef}
                >
                    <FontAwesomeIcon icon={faCircleXmark} className={styles.alertIcon} aria-hidden="true" />
                    <span className={styles.alertMessage}>
                        {state.bannerError ?? 'Check the highlighted fields below and try again.'}
                    </span>
                    {state.bannerError && (
                        <button
                            type="button"
                            className={styles.alertClose}
                            onClick={() => dispatch({ type: 'DISMISS_BANNER' })}
                            aria-label="Dismiss"
                        >
                            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                        </button>
                    )}
                </div>
            )}

            {/* Resend success notice — polite so it doesn't steal focus from the OTP input */}
            {state.resendNotice && (
                <div
                    className={cx(styles.alert, styles['alert--success'])}
                    role="status"
                    aria-live="polite"
                >
                    <FontAwesomeIcon icon={faCircleCheck} className={styles.alertIcon} aria-hidden="true" />
                    <span className={styles.alertMessage}>{state.resendNotice}</span>
                    <button
                        type="button"
                        className={styles.alertClose}
                        onClick={() => dispatch({ type: 'DISMISS_BANNER' })}
                        aria-label="Dismiss"
                    >
                        <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                    </button>
                </div>
            )}

            <div className={styles.fieldGroup}>
                <label htmlFor="senderName" className={styles.label}>
                    Your Name <span className={styles.requiredAsterisk} aria-hidden="true">*</span>
                </label>
                <input
                    id="senderName"
                    name="senderName"
                    type="text"
                    autoComplete="name"
                    className={cx(styles.field, { [styles['field--error']]: state.fieldErrors.name })}
                    placeholder="First and last name"
                    value={state.name}
                    onChange={(e) =>
                        dispatch({ type: 'SET_FIELD', field: 'name', value: e.target.value })
                    }
                    required
                    aria-invalid={state.fieldErrors.name !== null}
                    aria-describedby={state.fieldErrors.name ? 'error-name' : undefined}
                />
                {state.fieldErrors.name && (
                    <p id="error-name" className={styles.fieldError} role="alert">
                        {state.fieldErrors.name}
                    </p>
                )}
            </div>

            <div className={styles.fieldGroup}>
                <label htmlFor="senderEmail" className={styles.label}>
                    Your Email Address <span className={styles.requiredAsterisk} aria-hidden="true">*</span>
                </label>
                <input
                    id="senderEmail"
                    name="senderEmail"
                    type="email"
                    autoComplete="email"
                    className={cx(styles.field, { [styles['field--error']]: state.fieldErrors.email })}
                    placeholder="you@example.com"
                    value={state.email}
                    onChange={(e) =>
                        dispatch({ type: 'SET_FIELD', field: 'email', value: e.target.value })
                    }
                    onBlur={handleEmailBlur}
                    required
                    aria-invalid={state.fieldErrors.email !== null}
                    aria-describedby={
                        state.fieldErrors.email ? 'error-email' : 'email-hint'
                    }
                />
                {state.fieldErrors.email ? (
                    <p id="error-email" className={styles.fieldError} role="alert">
                        {state.fieldErrors.email}
                    </p>
                ) : (
                    <p id="email-hint" className={styles.fieldHint}>
                        We&apos;ll only use this to reply to you.
                    </p>
                )}
            </div>

            <div className={styles.fieldGroup}>
                <label htmlFor="senderPhone" className={styles.label}>
                    Your Phone Number <span className={styles.optional}>(Optional)</span>
                </label>
                <input
                    id="senderPhone"
                    name="senderPhone"
                    type="tel"
                    autoComplete="tel"
                    className={styles.field}
                    placeholder="(555) 555-5555"
                    value={state.phone}
                    onChange={(e) =>
                        dispatch({ type: 'SET_FIELD', field: 'phone', value: e.target.value })
                    }
                />
            </div>

            <div className={styles.fieldGroup}>
                <label htmlFor="subject" className={styles.label}>
                    Subject <span className={styles.requiredAsterisk} aria-hidden="true">*</span>
                </label>
                <input
                    id="subject"
                    name="subject"
                    type="text"
                    className={cx(styles.field, { [styles['field--error']]: state.fieldErrors.subject })}
                    value={state.subject}
                    onChange={(e) =>
                        dispatch({ type: 'SET_FIELD', field: 'subject', value: e.target.value })
                    }
                    required
                    aria-invalid={state.fieldErrors.subject !== null}
                    aria-describedby={state.fieldErrors.subject ? 'error-subject' : undefined}
                />
                {state.fieldErrors.subject && (
                    <p id="error-subject" className={styles.fieldError} role="alert">
                        {state.fieldErrors.subject}
                    </p>
                )}
            </div>

            <div className={styles.fieldGroup}>
                <label htmlFor="message" className={styles.label}>
                    Message <span className={styles.requiredAsterisk} aria-hidden="true">*</span>
                </label>
                <textarea
                    id="message"
                    name="message"
                    className={cx(styles.field, styles['field--textarea'], { [styles['field--error']]: state.fieldErrors.message })}
                    placeholder="How can we help?"
                    value={state.message}
                    onChange={(e) =>
                        dispatch({ type: 'SET_FIELD', field: 'message', value: e.target.value })
                    }
                    required
                    aria-invalid={state.fieldErrors.message !== null}
                    aria-describedby={state.fieldErrors.message ? 'error-message' : undefined}
                />
                {state.fieldErrors.message && (
                    <p id="error-message" className={styles.fieldError} role="alert">
                        {state.fieldErrors.message}
                    </p>
                )}
            </div>

            {showOtpSection && (
                <div className={styles.otpSection}>
                    <h3 className={styles.otpHeading}>
                        Please verify your email to send this message.
                    </h3>
                    {!!state.resendNotice && (
                        <p className={styles.otpSubtext}>
                            We sent a verification code to your email.
                        </p>
                    )}
                    <div className={styles.fieldGroup}>
                        <label htmlFor="otpCode" className={styles.label}>
                            Verification code
                        </label>
                        <input
                            id="otpCode"
                            name="otpCode"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            className={cx(styles.field, styles['field--otp'])}
                            value={state.otpCode}
                            onChange={(e) =>
                                dispatch({
                                    type: 'SET_FIELD',
                                    field: 'otpCode',
                                    value: e.target.value.replace(/\D/g, '').slice(0, 6),
                                })
                            }
                            placeholder="------"
                            maxLength={6}
                            required
                            ref={otpInputRef}
                            aria-describedby={state.otpError ? 'error-otp' : undefined}
                        />
                        {state.otpError !== null && (
                            <p id="error-otp" className={styles.fieldError} role="alert">
                                {state.otpError}
                            </p>
                        )}
                    </div>
                </div>
            )}

            <div className={styles.actions}>
                <button
                    type="submit"
                    className={styles.button}
                    disabled={!canSubmit}
                    aria-busy={state.phase === 'sending'}
                >
                    {showOtpSection ? 'Verify and send' : 'Send message'}
                </button>
                {showOtpSection && !state.resendHidden && (
                    <button
                        type="button"
                        className={styles.resendLink}
                        onClick={handleResend}
                        disabled={state.phase === 'otp-resending' || resendCoolingDown}
                    >
                        Resend code
                    </button>
                )}
                {showOtpSection && !state.resendHidden && resendCoolingDown && (
                    <span className={styles.resendCountdown}>
                        Resend available in {formatCountdown(resendSecondsLeft)}
                    </span>
                )}
            </div>

            <div className={styles.recaptchaNotice}>
                <img
                    src="https://www.gstatic.com/recaptcha/api2/logo_48.png"
                    alt=""
                    aria-hidden="true"
                    className={styles.recaptchaLogo}
                    width="24"
                    height="24"
                />
                <p className={styles.recaptchaText}>
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a
                        className={styles.recaptchaLink}
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a
                        className={styles.recaptchaLink}
                        href="https://policies.google.com/terms"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Terms of Service
                    </a>{' '}
                    apply.
                </p>
            </div>
        </form>
    );
}
